import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  Search, RefreshCw, Plus, Star, X, TrendingUp, TrendingDown, Clock,
  BarChart3, Globe, DollarSign, Gem, Activity, LayoutGrid
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, ResponsiveContainer, Area, AreaChart, XAxis, YAxis,
  Tooltip, ComposedChart
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#00e5a0', '#4f8eff', '#7c3aed', '#f97316', '#fbbf24', '#ec4899', '#06b6d4', '#84cc16'];

// ── TICKER CONFIGURATIONS ──
const INDICES = [
  { symbol: '^GSPC', name: 'S&P 500' }, { symbol: '^DJI', name: 'Dow Jones' },
  { symbol: '^IXIC', name: 'NASDAQ' }, { symbol: '^FCHI', name: 'CAC 40' },
  { symbol: '^GDAXI', name: 'DAX' }, { symbol: '^FTSE', name: 'FTSE 100' },
  { symbol: '^N225', name: 'Nikkei 225' }, { symbol: '^STOXX50E', name: 'Euro Stoxx 50' },
];
const STOCKS = [
  { symbol: 'AAPL', name: 'Apple' }, { symbol: 'MSFT', name: 'Microsoft' },
  { symbol: 'NVDA', name: 'NVIDIA' }, { symbol: 'GOOGL', name: 'Alphabet' },
  { symbol: 'AMZN', name: 'Amazon' }, { symbol: 'TSLA', name: 'Tesla' },
  { symbol: 'META', name: 'Meta' }, { symbol: 'MC.PA', name: 'LVMH' },
  { symbol: 'TTE.PA', name: 'TotalEnergies' }, { symbol: 'AI.PA', name: 'Air Liquide' },
  { symbol: 'BNP.PA', name: 'BNP Paribas' }, { symbol: 'SAN.PA', name: 'Sanofi' },
  { symbol: 'ASML.AS', name: 'ASML' }, { symbol: 'SAP.DE', name: 'SAP' },
  { symbol: 'NESN.SW', name: 'Nestlé' },
];
const CRYPTO = [
  { symbol: 'BTC-EUR', name: 'Bitcoin', cgId: 'bitcoin' },
  { symbol: 'ETH-EUR', name: 'Ethereum', cgId: 'ethereum' },
  { symbol: 'SOL-EUR', name: 'Solana', cgId: 'solana' },
  { symbol: 'BNB-EUR', name: 'BNB', cgId: 'binancecoin' },
  { symbol: 'XRP-EUR', name: 'XRP', cgId: 'ripple' },
  { symbol: 'ADA-EUR', name: 'Cardano', cgId: 'cardano' },
  { symbol: 'DOGE-EUR', name: 'Dogecoin', cgId: 'dogecoin' },
  { symbol: 'AVAX-EUR', name: 'Avalanche', cgId: 'avalanche-2' },
];
const COMMODITIES = [
  { symbol: 'GC=F', name: 'Or' }, { symbol: 'SI=F', name: 'Argent' },
  { symbol: 'CL=F', name: 'Pétrole WTI' }, { symbol: 'NG=F', name: 'Gaz Naturel' },
  { symbol: 'HG=F', name: 'Cuivre' }, { symbol: 'ZW=F', name: 'Blé' },
];
const CURRENCIES = [
  { symbol: 'EURUSD=X', name: 'EUR/USD' }, { symbol: 'EURGBP=X', name: 'EUR/GBP' },
  { symbol: 'EURJPY=X', name: 'EUR/JPY' }, { symbol: 'EURCHF=X', name: 'EUR/CHF' },
  { symbol: 'GBPUSD=X', name: 'GBP/USD' }, { symbol: 'USDJPY=X', name: 'USD/JPY' },
];
const HEATMAP_SECTORS = [
  { symbol: 'XLK', name: 'Tech', weight: 30 },
  { symbol: 'XLV', name: 'Santé', weight: 13 },
  { symbol: 'XLF', name: 'Finance', weight: 13 },
  { symbol: 'XLE', name: 'Énergie', weight: 10 },
  { symbol: 'XLI', name: 'Industrie', weight: 10 },
  { symbol: 'XLY', name: 'Conso. discr.', weight: 8 },
  { symbol: 'XLP', name: 'Conso. base', weight: 5 },
  { symbol: 'XLRE', name: 'Immobilier', weight: 4 },
  { symbol: 'XLU', name: 'Utilities', weight: 4 },
  { symbol: 'XLB', name: 'Matériaux', weight: 3 },
];

const fmt = (v, decimals = 2) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(v || 0);
const fmtCur = (v, cur = 'EUR') => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: cur, maximumFractionDigits: 2 }).format(v || 0);
const fmtBig = (v) => {
  if (!v) return '—';
  if (v >= 1e12) return `${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  return fmt(v, 0);
};

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const MarketsPage = () => {
  const { user, token } = useAuth();
  const userId = user?.id || 'anon';
  const lsKey = (k) => `capos_${userId}_mkt_${k}`;
  const lsGet = (k, fb) => { try { const v = localStorage.getItem(lsKey(k)); return v ? JSON.parse(v) : fb; } catch { return fb; } };
  const lsSet = (k, v) => localStorage.setItem(lsKey(k), JSON.stringify(v));

  // ── State ──
  const [tab, setTab] = useState('indices');
  const [watchlist, setWatchlist] = useState([]);
  const [quotesCache, setQuotesCache] = useState(() => lsGet('quotes_cache', {}));
  const [sparkCache, setSparkCache] = useState(() => lsGet('spark_cache', {}));
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailAsset, setDetailAsset] = useState(null);
  const [detailHistory, setDetailHistory] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPeriod, setDetailPeriod] = useState('1A');
  const fetchedRef = useRef(false);

  // ── Watchlist persistence: backend-first (cross-device), localStorage as cache ──
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/user-prefs/mkt_watchlist`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (Array.isArray(r.data?.value)) setWatchlist(r.data.value);
      })
      .catch(() => {});
  }, [token]);

  const saveWatchlist = (wl) => {
    setWatchlist(wl);
    if (token) {
      axios.put(`${API}/user-prefs/mkt_watchlist`, { value: wl },
        { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
  };
  const saveQuotes = (q) => { setQuotesCache(q); lsSet('quotes_cache', q); };
  const saveSparks = (s) => { setSparkCache(s); lsSet('spark_cache', s); };
  const isInWatchlist = (sym) => watchlist.some(w => w.symbol === sym);

  const displaySymbol = (sym) => sym.replace('^', '').replace('=X', '').replace('=F', '').replace('-EUR', '');
  const toggleWatchlist = (asset) => {
    if (isInWatchlist(asset.symbol)) {
      saveWatchlist(watchlist.filter(w => w.symbol !== asset.symbol));
      toast.success(`${asset.symbol} retiré de la watchlist`);
    } else {
      saveWatchlist([...watchlist, { symbol: asset.symbol, name: asset.name, type: asset.type || 'yahoo' }]);
      toast.success(`${asset.symbol} ajouté à la watchlist`);
    }
  };

  // ── All Yahoo symbols to fetch ──
  const allYahooSymbols = useMemo(() => {
    const syms = new Set();
    INDICES.forEach(i => syms.add(i.symbol));
    STOCKS.forEach(s => syms.add(s.symbol));
    CRYPTO.forEach(c => syms.add(c.symbol));
    COMMODITIES.forEach(c => syms.add(c.symbol));
    CURRENCIES.forEach(c => syms.add(c.symbol));
    HEATMAP_SECTORS.forEach(h => syms.add(h.symbol));
    watchlist.forEach(w => syms.add(w.symbol));
    return [...syms];
  }, [watchlist]);

  // ── Fetch all data ──
  const fetchAll = useCallback(async (force = false) => {
    const now = Date.now();
    const cacheValid = !force && quotesCache._ts && now - quotesCache._ts < CACHE_TTL;
    const sparksEmpty = Object.keys(sparkCache).filter(k => k !== '_ts').length === 0;
    if (cacheValid && !sparksEmpty) {
      setLastUpdate(new Date(quotesCache._ts));
      return;
    }
    setLoading(true);
    const newSparks = {};

    // Batch Yahoo quotes
    try {
      const batchSize = 15;
      const newQuotes = {};
      for (let i = 0; i < allYahooSymbols.length; i += batchSize) {
        const batch = allYahooSymbols.slice(i, i + batchSize).join(',');
        const res = await axios.get(`${API}/market/batch-quotes?symbols=${encodeURIComponent(batch)}`);
        Object.assign(newQuotes, res.data.quotes || {});
      }
      newQuotes._ts = now;
      saveQuotes(newQuotes);
    } catch (e) { console.error('Batch quotes error:', e); }

    // Sparklines for all yahoo symbols (including crypto)
    try {
      for (let i = 0; i < allYahooSymbols.length; i += 10) {
        const batch = allYahooSymbols.slice(i, i + 10).join(',');
        const res = await axios.get(`${API}/market/sparklines?symbols=${encodeURIComponent(batch)}`);
        Object.assign(newSparks, res.data.sparklines || {});
      }
    } catch (e) { console.error('Sparklines error:', e); }

    newSparks._ts = now;
    saveSparks(newSparks);
    setLastUpdate(new Date(now));
    setLoading(false);
  }, [allYahooSymbols]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fetch on mount
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Search ──
  const searchTimer = useRef(null);
  const doSearch = (q) => {
    setSearchQuery(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (q.length < 2) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axios.get(`${API}/market/search?q=${encodeURIComponent(q)}`);
        setSearchResults(res.data.results || []);
      } catch { setSearchResults([]); }
      setSearchLoading(false);
    }, 300);
  };

  // ── Detail modal ──
  const openDetail = async (symbol, name, type) => {
    setDetailAsset({ symbol, name, type });
    setDetailOpen(true);
    setDetailPeriod('1A');
    setDetailLoading(true);
    setDetailHistory([]);
    try {
      const res = await axios.get(`${API}/market/history/${encodeURIComponent(symbol)}?range=1y&interval=1d`);
      setDetailHistory(res.data?.history || []);
    } catch {}
    setDetailLoading(false);
  };

  const fetchDetailHistory = async (symbol, type, period) => {
    setDetailLoading(true);
    const yahooMap = {
      '1J': { range: '1d', interval: '5m' }, '5J': { range: '5d', interval: '15m' },
      '1M': { range: '1mo', interval: '1d' }, '3M': { range: '3mo', interval: '1d' },
      '6M': { range: '6mo', interval: '1d' }, 'YTD': { range: 'ytd', interval: '1d' },
      '1A': { range: '1y', interval: '1d' }, '3A': { range: '3y', interval: '1wk' },
      '5A': { range: '5y', interval: '1wk' }, 'MAX': { range: 'max', interval: '1mo' },
    };
    try {
      const { range, interval } = yahooMap[period] || yahooMap['1A'];
      const res = await axios.get(`${API}/market/history/${encodeURIComponent(symbol)}?range=${range}&interval=${interval}`);
      setDetailHistory(res.data?.history || []);
    } catch {}
    setDetailLoading(false);
  };

  useEffect(() => {
    if (detailOpen && detailAsset) fetchDetailHistory(detailAsset.symbol, detailAsset.type, detailPeriod);
  }, [detailPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resolve quote data (all from Yahoo Finance now) ──
  const getQuote = (symbol) => {
    const q = quotesCache[symbol];
    if (!q) return null;
    // Clean crypto names: "Bitcoin EUR" → "Bitcoin"
    if (q.name && symbol.endsWith('-EUR')) {
      q.name = q.name.replace(/ EUR$/, '');
    }
    return q;
  };
  const getSpark = (symbol) => sparkCache[symbol] || [];

  // ── Sparkline component (SVG) ──
  const Sparkline = ({ data, positive }) => {
    if (!data || data.length < 2) return <div className="w-full h-full bg-white/5 rounded" />;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const w = 100;
    const h = 100;
    const points = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ');
    const color = positive ? '#00e5a0' : '#ef4444';
    return (
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-full">
        <polyline points={points} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
      </svg>
    );
  };

  // ── Asset Card ──
  const AssetCard = ({ symbol, name, type = 'yahoo' }) => {
    const q = getQuote(symbol);
    const spark = getSpark(symbol);
    const positive = q ? q.changePercent >= 0 : true;
    const currency = q?.currency || 'EUR';
    const priceDecimals = (q?.price || 0) < 10 ? 4 : 2;

    return (
      <div
        className="glass-card p-4 cursor-pointer hover:bg-white/[0.04] transition-all group relative"
        onClick={() => openDetail(symbol, name, type)}
        data-testid={`card-${symbol}`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="min-w-0">
            <p className="text-white font-semibold text-sm truncate">{q?.name || name}</p>
            <p className="text-cap-text-muted text-xs truncate">{displaySymbol(symbol)}</p>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded flex items-center gap-0.5 ${positive ? 'bg-cap-emerald/10 text-cap-emerald' : 'bg-cap-error/10 text-cap-error'}`}>
              {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {q ? `${q.changePercent >= 0 ? '+' : ''}${fmt(q.changePercent)}%` : '—'}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); toggleWatchlist({ symbol, name, type }); }}
              className={`p-1 rounded transition-all ${isInWatchlist(symbol) ? 'text-yellow-400' : 'text-cap-text-muted opacity-0 group-hover:opacity-100 hover:text-yellow-400'}`}
              data-testid={`star-${symbol}`}
            >
              <Star className={`w-3.5 h-3.5 ${isInWatchlist(symbol) ? 'fill-current' : ''}`} />
            </button>
          </div>
        </div>
        <div className="h-10 mb-2">
          <Sparkline data={spark} positive={positive} />
        </div>
        <div className="flex items-end justify-between">
          <p className="text-white font-display text-lg font-bold">{q ? fmt(q.price, priceDecimals) : '—'}</p>
          {q && <p className={`text-xs font-medium ${positive ? 'text-cap-emerald' : 'text-cap-error'}`}>{q.change >= 0 ? '+' : ''}{fmt(q.change, priceDecimals)}</p>}
        </div>
        {lastUpdate && <p className="text-cap-text-muted/40 text-[9px] mt-1 flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>}
      </div>
    );
  };

  // ── Heatmap ──
  const HeatmapView = () => {
    const totalWeight = HEATMAP_SECTORS.reduce((s, h) => s + h.weight, 0);
    return (
      <div className="space-y-4">
        <p className="text-cap-text-muted text-sm">Variation du jour par secteur (ETF S&P 500)</p>
        <div className="grid grid-cols-5 gap-1.5" data-testid="heatmap-grid">
          {HEATMAP_SECTORS.map(sector => {
            const q = quotesCache[sector.symbol];
            const pct = q?.changePercent || 0;
            let bg;
            if (pct > 2) bg = '#166534';
            else if (pct > 1) bg = '#00e5a0';
            else if (pct > 0) bg = '#34d399';
            else if (pct > -1) bg = '#fca5a5';
            else if (pct > -2) bg = '#ef4444';
            else bg = '#991b1b';
            const textColor = (pct > 0 && pct <= 2) ? '#0d0f14' : '#ffffff';
            const h = Math.max(80, (sector.weight / totalWeight) * 600);
            return (
              <TooltipProvider key={sector.symbol}>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="rounded-lg p-3 flex flex-col justify-between cursor-pointer transition-transform hover:scale-[1.02]"
                      style={{ backgroundColor: bg, minHeight: h, gridColumn: sector.weight >= 13 ? 'span 2' : 'span 1' }}
                      onClick={() => openDetail(sector.symbol, sector.name, 'yahoo')}
                      data-testid={`heatmap-${sector.symbol}`}
                    >
                      <p className="font-semibold text-sm" style={{ color: textColor }}>{sector.name}</p>
                      <div>
                        <p className="font-display text-xl font-bold" style={{ color: textColor }}>{pct >= 0 ? '+' : ''}{fmt(pct)}%</p>
                        <p className="text-xs opacity-70" style={{ color: textColor }}>{sector.symbol}</p>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="bg-cap-bg-elevated border-white/10 text-white">
                    <p className="font-semibold">{sector.name} ({sector.symbol})</p>
                    <p>Variation jour : {pct >= 0 ? '+' : ''}{fmt(pct)}%</p>
                    {q && <p>Prix : {fmt(q.price, 2)}</p>}
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Filtered detail history ──
  const filteredDetailHistory = useMemo(() => {
    if (!detailHistory?.length) return [];
    // For short periods, data is already filtered by the API
    if (['1J', '5J', '1M', '3M', '6M'].includes(detailPeriod)) return detailHistory;
    const now = new Date();
    let cutoff;
    if (detailPeriod === 'YTD') cutoff = new Date(now.getFullYear(), 0, 1);
    else if (detailPeriod === '1A') cutoff = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    else if (detailPeriod === '3A') cutoff = new Date(now.getFullYear() - 3, now.getMonth(), now.getDate());
    else if (detailPeriod === '5A') cutoff = new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
    else return detailHistory;
    return detailHistory.filter(d => new Date(d.date) >= cutoff);
  }, [detailHistory, detailPeriod]);

  // ── Tab icon map ──
  const tabMeta = [
    { v: 'indices', l: 'Indices', icon: BarChart3 },
    { v: 'actions', l: 'Actions', icon: TrendingUp },
    { v: 'crypto', l: 'Crypto', icon: Gem },
    { v: 'commodities', l: 'Matières Prem.', icon: Activity },
    { v: 'currencies', l: 'Devises', icon: DollarSign },
    { v: 'heatmap', l: 'Heatmap', icon: LayoutGrid },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="markets-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Marchés & Indices</h1>
          <p className="text-cap-text-muted text-sm mt-1">Données en temps réel via Yahoo Finance</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdate && (
            <p className="text-cap-text-muted text-xs flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Mis à jour à {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
          <Button onClick={() => fetchAll(true)} disabled={loading} size="sm" variant="outline" className="border-white/10 text-cap-text-muted hover:text-white" data-testid="refresh-all">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> {loading ? 'Chargement...' : 'Actualiser'}
          </Button>
          {/* Search trigger */}
          <div className="relative">
            <div className="flex items-center gap-2 bg-cap-bg border border-white/10 rounded-lg px-3 py-1.5 w-64 cursor-text" onClick={() => setSearchOpen(true)} data-testid="search-trigger">
              <Search className="w-4 h-4 text-cap-text-muted shrink-0" />
              <span className="text-cap-text-muted text-sm">Rechercher un actif...</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── WATCHLIST ── */}
      {watchlist.length > 0 && (
        <div data-testid="watchlist-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-semibold flex items-center gap-2"><Star className="w-4 h-4 text-yellow-400 fill-yellow-400" /> Ma Watchlist</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {watchlist.map(w => (
              <AssetCard key={w.symbol} symbol={w.symbol} name={w.name} type={w.type || 'yahoo'} />
            ))}
            <button
              onClick={() => setSearchOpen(true)}
              className="glass-card p-4 flex items-center justify-center gap-2 text-cap-text-muted hover:text-white hover:bg-white/[0.04] transition-all border-dashed border border-white/10 min-h-[140px]"
              data-testid="add-watchlist-btn"
            >
              <Plus className="w-5 h-5" /> Ajouter
            </button>
          </div>
        </div>
      )}
      {watchlist.length === 0 && (
        <div className="glass-card p-6 flex items-center justify-between" data-testid="empty-watchlist">
          <div>
            <p className="text-white font-semibold flex items-center gap-2"><Star className="w-4 h-4 text-yellow-400" /> Ma Watchlist</p>
            <p className="text-cap-text-muted text-sm mt-1">Ajoutez des actifs à votre watchlist pour les suivre ici</p>
          </div>
          <Button onClick={() => setSearchOpen(true)} size="sm" className="bg-cap-emerald/20 text-cap-emerald hover:bg-cap-emerald/30" data-testid="add-watchlist-btn-empty">
            <Plus className="w-4 h-4 mr-1" /> Ajouter à la watchlist
          </Button>
        </div>
      )}

      {/* ── TABS ── */}
      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="bg-cap-bg-elevated p-1 rounded-xl border border-white/5 flex gap-1 overflow-x-auto">
          {tabMeta.map(t => (
            <TabsTrigger key={t.v} value={t.v} className="flex-1 rounded-lg data-[state=active]:bg-white/10 text-cap-text-muted data-[state=active]:text-white text-sm whitespace-nowrap px-3 flex items-center gap-1.5" data-testid={`tab-${t.v}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Indices */}
        <TabsContent value="indices" data-testid="tab-content-indices">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {INDICES.map(i => <AssetCard key={i.symbol} symbol={i.symbol} name={i.name} />)}
          </div>
        </TabsContent>

        {/* Actions */}
        <TabsContent value="actions" data-testid="tab-content-actions">
          <div className="space-y-4">
            <h3 className="text-cap-text-muted text-xs uppercase tracking-wider">US</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {STOCKS.filter(s => !s.symbol.includes('.')).map(s => <AssetCard key={s.symbol} symbol={s.symbol} name={s.name} />)}
            </div>
            <h3 className="text-cap-text-muted text-xs uppercase tracking-wider mt-6">Europe</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {STOCKS.filter(s => s.symbol.includes('.')).map(s => <AssetCard key={s.symbol} symbol={s.symbol} name={s.name} />)}
            </div>
          </div>
        </TabsContent>

        {/* Crypto */}
        <TabsContent value="crypto" data-testid="tab-content-crypto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {CRYPTO.map(c => <AssetCard key={c.symbol} symbol={c.symbol} name={c.name} />)}
          </div>
        </TabsContent>

        {/* Commodities */}
        <TabsContent value="commodities" data-testid="tab-content-commodities">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {COMMODITIES.map(c => <AssetCard key={c.symbol} symbol={c.symbol} name={c.name} />)}
          </div>
        </TabsContent>

        {/* Currencies */}
        <TabsContent value="currencies" data-testid="tab-content-currencies">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {CURRENCIES.map(c => <AssetCard key={c.symbol} symbol={c.symbol} name={c.name} />)}
          </div>
        </TabsContent>

        {/* Heatmap */}
        <TabsContent value="heatmap" data-testid="tab-content-heatmap">
          <HeatmapView />
        </TabsContent>
      </Tabs>

      {/* ═══ SEARCH DIALOG ═══ */}
      <Dialog open={searchOpen} onOpenChange={(o) => { setSearchOpen(o); if (!o) { setSearchQuery(''); setSearchResults([]); } }}>
        <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-md p-0">
          <DialogHeader className="px-5 pt-5 pb-0"><DialogTitle className="text-base">Rechercher un actif</DialogTitle></DialogHeader>
          <div className="px-5 py-3">
            <div className="flex items-center gap-2 bg-cap-bg border border-white/10 rounded-lg px-3 py-2">
              <Search className="w-4 h-4 text-cap-text-muted shrink-0" />
              <input
                value={searchQuery}
                onChange={e => doSearch(e.target.value)}
                placeholder="Nom ou ticker (ex: Apple, BTC, CAC)..."
                className="bg-transparent text-white text-sm outline-none w-full placeholder:text-cap-text-muted"
                autoFocus
                data-testid="search-input"
              />
              {searchQuery && <button onClick={() => { setSearchQuery(''); setSearchResults([]); }}><X className="w-4 h-4 text-cap-text-muted hover:text-white" /></button>}
            </div>
          </div>
          <div className="max-h-[300px] overflow-y-auto px-2 pb-4">
            {searchLoading && <p className="text-center text-cap-text-muted text-sm py-4">Recherche...</p>}
            {!searchLoading && searchResults.length === 0 && searchQuery.length >= 2 && <p className="text-center text-cap-text-muted text-sm py-4">Aucun résultat</p>}
            {searchResults.map(r => (
              <button
                key={r.symbol}
                onClick={() => { setSearchOpen(false); setSearchQuery(''); setSearchResults([]); openDetail(r.symbol, r.name, 'yahoo'); }}
                className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-left"
                data-testid={`search-result-${r.symbol}`}
              >
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{r.symbol}</p>
                  <p className="text-cap-text-muted text-xs truncate">{r.name}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-cap-text-muted text-[10px] px-1.5 py-0.5 rounded bg-white/5">{r.type}</span>
                  <span className="text-cap-text-muted text-[10px]">{r.exchange}</span>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ DETAIL MODAL ═══ */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-cap-bg-elevated border-white/10 text-white p-0 max-w-3xl w-[90vw] max-h-[85vh] overflow-y-auto" data-testid="detail-modal">
          <DialogTitle className="sr-only">Détail</DialogTitle>
          {detailAsset && (() => {
            const q = getQuote(detailAsset.symbol) || {};
            const positive = (q.changePercent || 0) >= 0;
            const priceDecimals = (q.price || 0) < 10 ? 4 : 2;
            return (
              <div>
                {/* Header */}
                <div className="px-6 pt-6 pb-4 border-b border-white/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="font-display text-xl font-bold text-white">{q.name || detailAsset.name}</h2>
                      <p className="text-cap-text-muted text-sm">{detailAsset.symbol}{q.exchange ? ` · ${q.exchange}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-display text-2xl font-bold text-white">{q.price ? fmt(q.price, priceDecimals) : '—'}</p>
                        <p className={`text-sm font-medium ${positive ? 'text-cap-emerald' : 'text-cap-error'}`}>
                          {q.change !== undefined ? `${q.change >= 0 ? '+' : ''}${fmt(q.change, priceDecimals)}` : ''} ({q.changePercent !== undefined ? `${q.changePercent >= 0 ? '+' : ''}${fmt(q.changePercent)}%` : '—'})
                        </p>
                      </div>
                      <button
                        onClick={() => toggleWatchlist({ symbol: detailAsset.symbol, name: q.name || detailAsset.name, type: detailAsset.type })}
                        className={`p-2 rounded-lg transition-all ${isInWatchlist(detailAsset.symbol) ? 'text-yellow-400 bg-yellow-400/10' : 'text-cap-text-muted hover:text-yellow-400 bg-white/5'}`}
                        data-testid="detail-star"
                      >
                        <Star className={`w-5 h-5 ${isInWatchlist(detailAsset.symbol) ? 'fill-current' : ''}`} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Chart */}
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold text-sm">Historique</h3>
                    <div className="flex gap-1">
                      {['1J', '5J', '1M', '3M', '6M', 'YTD', '1A', '3A', '5A', 'MAX'].map(p => (
                        <button key={p} onClick={() => setDetailPeriod(p)} className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${detailPeriod === p ? 'bg-cap-emerald text-cap-bg' : 'bg-cap-bg text-cap-text-muted hover:text-white'}`} data-testid={`detail-period-${p}`}>{p}</button>
                      ))}
                    </div>
                  </div>
                  {detailLoading ? (
                    <div className="flex items-center justify-center h-56 text-cap-text-muted text-sm"><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Chargement...</div>
                  ) : filteredDetailHistory.length > 0 ? (
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={filteredDetailHistory}>
                          <defs>
                            <linearGradient id="detailGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={positive ? '#00e5a0' : '#ef4444'} stopOpacity={0.15} />
                              <stop offset="95%" stopColor={positive ? '#00e5a0' : '#ef4444'} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="date" stroke="#64748b" fontSize={10} interval="preserveStartEnd" tickFormatter={d => d?.slice(5)} />
                          <YAxis stroke="#64748b" fontSize={10} domain={['auto', 'auto']} tickFormatter={v => fmt(v, 0)} />
                          <Tooltip contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '11px' }} formatter={v => fmt(v, priceDecimals)} labelFormatter={l => l} />
                          <Area type="monotone" dataKey="close" stroke={positive ? '#00e5a0' : '#ef4444'} fill="url(#detailGrad)" strokeWidth={2} dot={false} name="Cours" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : <p className="text-cap-text-muted text-sm text-center py-12">Aucune donnée historique</p>}
                </div>

                {/* Info block */}
                {q.price && (
                  <div className="px-6 pb-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="detail-info">
                      {[
                        { l: '52 sem. haut', v: q.fiftyTwoWeekHigh ? fmt(q.fiftyTwoWeekHigh, priceDecimals) : '—' },
                        { l: '52 sem. bas', v: q.fiftyTwoWeekLow ? fmt(q.fiftyTwoWeekLow, priceDecimals) : '—' },
                        { l: 'Volume', v: q.volume ? fmtBig(q.volume) : '—' },
                        { l: 'Clôture préc.', v: q.previousClose ? fmt(q.previousClose, priceDecimals) : '—' },
                        { l: 'Plus haut jour', v: q.dayHigh ? fmt(q.dayHigh, priceDecimals) : '—' },
                        { l: 'Plus bas jour', v: q.dayLow ? fmt(q.dayLow, priceDecimals) : '—' },
                        { l: 'Devise', v: q.currency || '—' },
                        { l: 'Marché', v: q.exchange || '—' },
                      ].map(k => (
                        <div key={k.l} className="p-3 bg-cap-bg rounded-xl">
                          <p className="text-cap-text-muted text-xs mb-0.5">{k.l}</p>
                          <p className="text-white font-semibold text-sm">{k.v}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketsPage;
